import { SkyButton, SkyCard, SkyText, SkyTextarea } from "@skypointcloud/skypoint-ui";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { MAX_RULE_EMAILS } from "@shared/types/notifications";
import {
  SECURITY_CATEGORIES,
  SECURITY_CATEGORY_LABELS,
  type SecurityCategory,
  isAlwaysSentSecurityCategory,
} from "@shared/types/securityAlerts";
import { SingleCheckbox } from "@/components/ui/SingleCheckbox";
import { useL1Recipients, useSaveL1Recipients } from "@/hooks/use-notifications";
import { ApiClientError } from "@/lib/api";
import { InlineErrorBar, useInlineError, type InlineFailure } from "@/lib/feedback/InlineErrorBar";
import { useDismissNoticeWhen, useNotice } from "@/lib/feedback/NoticeProvider";
import { emailLines, emailsProblem } from "@/lib/notifications/rulesDraft";
import { ErrorState } from "./ScreenState";

const SAVED_NOTICE_KEY = "l1-recipients-saved";
const SECURITY_ONLY_SAVED_MESSAGE = "L1 security alerts saved. They apply to the next event.";

/** `chosen` with `category` ticked or unticked, in `SECURITY_CATEGORIES` order. */
function toggledCategory(
  chosen: readonly SecurityCategory[],
  category: SecurityCategory,
  on: boolean,
): SecurityCategory[] {
  const next = new Set(chosen);
  if (on) next.add(category);
  else next.delete(category);
  return SECURITY_CATEGORIES.filter((item) => next.has(item));
}

/** The refused save, with Reload when the answer was stale (review r2 S2). */
function SaveErrorWithReload({
  error,
  stale,
  onDismiss,
  onReload,
}: {
  error: InlineFailure;
  stale: boolean;
  onDismiss: () => void;
  onReload: () => void;
}) {
  return (
    <div className="flex flex-col items-start gap-2">
      <InlineErrorBar error={error} testId="l1-recipients-save-error" onDismiss={onDismiss} />
      {stale ? (
        <SkyButton
          appearance="secondary"
          size="small"
          testId="reload-l1-recipients"
          onClick={onReload}
        >
          Reload
        </SkyButton>
      ) : null}
    </div>
  );
}

/**
 * Platform settings > General > L1 email recipients (contract-notif R5, R9; round-1 ruling 4).
 *
 * The L1 team's addresses, in the module's own table and route, never in the platform settings
 * document, so this card has its own "Save L1 recipients" and does not join the page's save bar.
 * Every failure (a failed run, a rejected or expired credential, a failed data-health check)
 * reaches L1 once an address is set; there is no per-rule switch (round 3, 2026-09-30; platform
 * migration `notification_rules_drop_l1`, 0020, drops the `notification_rules.l1` column).
 * The audit row carries counts only.
 *
 * Security alerts (2026-09-29): which security categories the L1 team also receives, off until a
 * platform admin ticks them. The L1 team takes a deployment-wide alert and a tenant's break-glass,
 * never a tenant's other categories (those go to the tenant's own admins). The categories are sent
 * only when they changed, so a save that touched only the addresses leaves them as stored.
 */
export function L1RecipientsCard() {
  const l1 = useL1Recipients();
  const save = useSaveL1Recipients();
  const notice = useNotice();
  const saveError = useInlineError();
  const [text, setText] = useState<string | null>(null);
  const [security, setSecurity] = useState<SecurityCategory[] | null>(null);
  // The `updatedAt` the edit started from (review r1 S1): a background read that lands while the
  // box is being edited must not move the version Save sends, or another admin's change would be
  // overwritten without the stale answer. Undefined while nothing is being edited.
  const [editedFrom, setEditedFrom] = useState<string | null | undefined>(undefined);
  const [serverProblem, setServerProblem] = useState<string | null>(null);
  // A stale answer (review r2 S2): `editedFrom` would send the same old version forever, so the
  // card offers Reload, which reads the list again and starts the edit over from it.
  const [stale, setStale] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const focusBox = useRef(false);

  // R14: Reload unmounts itself; focus goes to the box it just refilled.
  useEffect(() => {
    if (!focusBox.current) return;
    const target = cardRef.current?.querySelector<HTMLElement>(
      '[data-testid="l1-recipients"] textarea',
    );
    if (!target) return;
    focusBox.current = false;
    target.focus();
  });

  const stored = l1.data ? l1.data.emails.join("\n") : "";
  const value = text ?? stored;
  const problem = emailsProblem(value) ?? serverProblem;
  const storedSecurity = l1.data?.securityCategories ?? [];
  const chosenSecurity = security ?? storedSecurity;
  const securityDirty = security !== null && security.join(",") !== storedSecurity.join(",");
  const emailsDirty =
    text !== null && emailLines(text).join("\n") !== emailLines(stored).join("\n");
  const dirty = emailsDirty || securityDirty;
  // ux-s10 §3: "saved" is never shown over unsaved edits (as the Notifications tab does).
  useDismissNoticeWhen(SAVED_NOTICE_KEY, dirty);

  function handleSave() {
    if (!l1.data || emailsProblem(value)) return;
    // A save of the security ticks alone says so; anything else is the recipients' message.
    const savedMessage =
      securityDirty && !emailsDirty
        ? SECURITY_ONLY_SAVED_MESSAGE
        : "L1 recipients saved. They apply to the next run that finishes.";
    saveError.clear();
    setServerProblem(null);
    setStale(false);
    save.mutate(
      {
        emails: emailLines(value).map((line) => line.toLowerCase()),
        expectedUpdatedAt: editedFrom === undefined ? l1.data.updatedAt : editedFrom,
        ...(securityDirty ? { securityCategories: chosenSecurity } : {}),
      },
      {
        onSuccess: () => {
          setText(null);
          setSecurity(null);
          setEditedFrom(undefined);
          notice.success(savedMessage, { key: SAVED_NOTICE_KEY });
        },
        onError: (error) => {
          if (error instanceof ApiClientError && error.code === "STALE_UPDATE") setStale(true);
          if (error instanceof ApiClientError && error.code === "VALIDATION_FAILED") {
            const lines = error.details
              .map((detail) => {
                const match = /^emails\.(\d+)$/.exec(detail.field ?? "");
                return match ? `Line ${Number(match[1]) + 1}: ${detail.issue}` : null;
              })
              .filter((line): line is string => line !== null);
            if (lines.length > 0) setServerProblem(lines.join(" "));
          }
          saveError.show(error, "The L1 recipients could not be saved.");
        },
      },
    );
  }

  async function reload() {
    saveError.clear();
    setServerProblem(null);
    const result = await l1.refetch();
    if (result.data) {
      setStale(false);
      setText(null);
      setSecurity(null);
      setEditedFrom(undefined);
      focusBox.current = true;
    }
  }

  function renderRecipients(): ReactNode {
    if (l1.isPending) {
      return (
        // A line, not the library skeleton (see EmailDeliveryCard).
        <SkyText size={200} color="muted" block role="status" testId="l1-recipients-loading">
          Loading L1 recipients…
        </SkyText>
      );
    }
    if (l1.isError) {
      return (
        <ErrorState
          title="L1 recipients did not load"
          error={l1.error}
          onRetry={() => void l1.refetch()}
          testId="l1-recipients-error"
        />
      );
    }
    return (
      <>
        <SkyTextarea
          label="L1 addresses"
          value={value}
          rows={4}
          hint={`One address per line, up to ${MAX_RULE_EMAILS}`}
          validationState={problem ? "error" : "none"}
          validationMessage={problem ?? undefined}
          testId="l1-recipients"
          onChange={(event) => {
            if (editedFrom === undefined) setEditedFrom(l1.data?.updatedAt ?? null);
            setText(event.target.value);
            setServerProblem(null);
          }}
        />
        <fieldset className="flex flex-col gap-2" data-testid="l1-security-categories">
          <legend className="mb-2">
            <SkyText size={300} weight="semibold" block>
              Also send these security alerts to the L1 team
            </SkyText>
            <SkyText size={200} color="muted" block>
              Deployment-wide alerts and any tenant&apos;s break-glass. A tenant&apos;s other alerts
              go to its own admins.
            </SkyText>
          </legend>
          {/* An always-sent category (audit integrity, wave 4 answer Q2) goes to the L1 team
                  whatever is ticked here, so it is listed as a note rather than a choice. */}
          {SECURITY_CATEGORIES.filter((category) => !isAlwaysSentSecurityCategory(category)).map(
            (category) => (
              <SingleCheckbox
                key={category}
                label={SECURITY_CATEGORY_LABELS[category]}
                checked={chosenSecurity.includes(category)}
                onChange={(on) => {
                  if (editedFrom === undefined) setEditedFrom(l1.data?.updatedAt ?? null);
                  setSecurity(toggledCategory(chosenSecurity, category, on));
                }}
                testId={`l1-security-${category}`}
              />
            ),
          )}
          <SkyText size={200} color="muted" block testId="l1-security-always">
            {`${SECURITY_CATEGORY_LABELS.audit_integrity} alerts always go to the L1 team.`}
          </SkyText>
        </fieldset>
        {saveError.error ? (
          <SaveErrorWithReload
            error={saveError.error}
            stale={stale}
            onDismiss={saveError.clear}
            onReload={() => void reload()}
          />
        ) : null}
        <div>
          <SkyButton
            appearance="primary"
            disabled={!dirty || emailsProblem(value) !== null}
            loading={save.isPending}
            testId="save-l1-recipients"
            onClick={handleSave}
          >
            Save L1 recipients
          </SkyButton>
        </div>
      </>
    );
  }

  return (
    <SkyCard appearance="outline" testId="l1-recipients-card">
      <div className="flex flex-col gap-3 p-1" ref={cardRef}>
        <div>
          <SkyText as="h2" size={400} weight="semibold" block>
            L1 email recipients
          </SkyText>
          <SkyText size={200} color="muted" block>
            Every failure on this deployment is emailed here once an address is set: a failed run, a
            rejected or expired credential, a failed data-health check. Security alerts follow the
            categories ticked below.
          </SkyText>
        </div>

        {renderRecipients()}
      </div>
    </SkyCard>
  );
}
